import React, { ChangeEvent, useRef, useState } from 'react';
import { Avatar } from 'geniebook-ui/dist';
import Pen from '@assets/icons/pen';
import moment from 'moment';
import { useMutation } from '@tanstack/react-query';
import UserServices from '@services/GeniebookAPI/UserServices.services';
import { Snackbar } from 'geniebook-ui/dist';
import { useViewWidth } from '@utils/helpers/viewWidth';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

export interface ProfilePictureProps {
  name: string;
  studentId: string | number;
  pp_ext: string;
  imageUrl?: string;
  imageEdited?: (onResolve: () => void) => void;
  isLoading?: boolean;
}

const ProfilePicture = (props: ProfilePictureProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [unixIdentifier, setUnixIdentifier] = useState(moment().unix());
  const [showSnackBar, setShowSnackBar] = useState<boolean>(false);
  const viewWidth = useViewWidth();
  const [errorMessage, setErrorMessage] = useState<string>(
    'Oops! Something went wrong. Please try again later.',
  );
  const gcsBucket = process.env.GCS_BUCKET;

  const updateProfilePicture = useMutation(
    (formData: FormData) => UserServices.uploadProfileImage(formData),
    {
      onSuccess(data) {
        if (data.data.error) {
          setErrorMessage(data.data.error_msg);

          setShowSnackBar(true);
        } else {
          props.imageEdited &&
            props.imageEdited(() => {
              // only refresh the timestamps when successfully reload the user image
              setUnixIdentifier(moment().unix());
            });
        }
      },
      onError() {
        setShowSnackBar(true);
      },
    },
  );

  const handleFileChange = async (event: ChangeEvent) => {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (file) {
      const formData = new FormData();
      formData.append('image', file);

      await updateProfilePicture.mutateAsync(formData);
    }
  };

  const handleChangeImageButton = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={'w-full'}>
      {showSnackBar ? (
        <Snackbar
          message={errorMessage}
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText={'Dismiss'}
          onTextClick={() => {
            setShowSnackBar(false);
          }}
          states={'error'}
          className={'fixed center-horizontal top-[60px]'}
        />
      ) : (
        <></>
      )}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <div
        className={'w-[88px] mx-auto relative cursor-pointer'}
        onClick={handleChangeImageButton}
      >
        {gcsBucket && !props.isLoading ? (
          <Avatar
            size={88}
            alt={props.name}
            src={
              (props.imageUrl
                ? props.imageUrl
                : gcsBucket +
                  'images/profile/student/' +
                  props.studentId +
                  '_thumb' +
                  props.pp_ext) +
              '?timestamp=' +
              unixIdentifier
            }
            className={'mb-2 block'}
            studentId={props.studentId}
          />
        ) : (
          <></>
        )}
        <div
          className={
            'absolute rounded-full bg-primary-apricot w-[28px] h-[28px] top-[4px] right-[-8px]'
          }
        >
          <Pen className={'fill-current text-neutral-chalk w-[28px] p-1.5'} />
        </div>
      </div>
      <div
        className={
          'mb-2 font-black text-lg text-center text-neutral-whiteboard leading-5'
        }
      >
        {props.name}
      </div>
      <div className={'text-center text-neutral-whiteboard'}>
        Student ID: {props.studentId}
      </div>
    </div>
  );
};

export default ProfilePicture;
